import FilePreviewContent from "./FilePreviewContent";
import { useEffect, useMemo, useRef } from "react";

const normalizeName = (file = {}) => {
  const raw =
    file?.name ||
    file?.filename ||
    (typeof file?.url === "string" ? file.url.split("?")[0].split("/").pop() : "") ||
    "";
  return String(raw || "File").trim() || "File";
};

const downloadFromBlob = (blob, fileName) => {
  if (!blob) return;
  let url = null;
  try {
    url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "download";
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    if (url) {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    }
  }
};

const FilePreviewPanel = ({ file, onClose }) => {
  const bodyRef = useRef(null);

  const isPdf = useMemo(() => {
    if (!file) return false;
    const mime = String(file?.mimeType || file?.mime || "").toLowerCase();
    if (mime === "application/pdf") return true;
    const raw =
      file?.name ||
      file?.filename ||
      (typeof file?.url === "string"
        ? file.url.split("?")[0].split("#")[0].split("/").pop()
        : "") ||
      "";
    return String(raw).toLowerCase().endsWith(".pdf");
  }, [file]);

  useEffect(() => {
    // Ensure each preview starts at the top; avoids "clipped" top content from
    // preserved scroll position on subsequent opens.
    try {
      const el = bodyRef.current;
      if (el) el.scrollTop = 0;
    } catch {}
  }, [file?.url, file?.name, file?.filename, file?.blob]);

  if (!file) return null;

  const name = normalizeName(file);
  const canDownload = Boolean(file?.url) || Boolean(file?.blob);

  const onDownload = () => {
    if (file?.blob) {
      downloadFromBlob(file.blob, name);
      return;
    }
    if (file?.url) {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = name;
      link.rel = "noopener noreferrer";
      link.target = "_blank";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <aside className="file-preview-panel" aria-label="File preview">
      <div className="file-preview-panel__header">
        <div className="file-preview-panel__title" title={name}>
          {name}
        </div>
        <div className="file-preview-panel__actions">
          <button
            type="button"
            className="file-preview-panel__btn"
            onClick={onDownload}
            disabled={!canDownload}
            title={canDownload ? "Download" : "Download unavailable"}
          >
            Download
          </button>
          <button
            type="button"
            className="file-preview-panel__btn file-preview-panel__btn--close"
            onClick={onClose}
            title="Close preview"
          >
            ✕
          </button>
        </div>
      </div>

      <div
        ref={bodyRef}
        className={`file-preview-panel__body ${
          isPdf ? "file-preview-panel__body--pdf" : ""
        }`}
      >
        <FilePreviewContent file={file} />
      </div>
    </aside>
  );
};

export default FilePreviewPanel;
